import { memo, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { X, PlusCircle } from 'react-feather';
import { Check } from '../icons/Check';
import { XCircle } from '../icons/XCircle';

type ChatConfigFormProps = {
  initialMessage: string;
  initialQuestions: string[];
  onSave: (data: { initialMessage: string | null; questions: string[] }) => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
};

const ChatConfigFormComponent = ({
  initialMessage,
  initialQuestions,
  onSave,
  onCancel,
  isSaving = false,
}: ChatConfigFormProps) => {
  const [draftMessage, setDraftMessage] = useState(initialMessage);
  const [draftQuestions, setDraftQuestions] = useState<string[]>(initialQuestions);

  const hasChanges = useMemo(() => {
    if (draftMessage !== initialMessage) return true;
    if (draftQuestions.length !== initialQuestions.length) return true;
    return draftQuestions.some((q, i) => q !== initialQuestions[i]);
  }, [draftMessage, initialMessage, draftQuestions, initialQuestions]);

  const handleSave = useCallback(async () => {
    const trimmedMessage = draftMessage.trim() || null;
    const trimmedQuestions = draftQuestions
      .map(q => q.trim())
      .filter(q => q.length > 0);
    await onSave({
      initialMessage: trimmedMessage,
      questions: trimmedQuestions,
    });
  }, [draftMessage, draftQuestions, onSave]);

  const handleQuestionChange = useCallback((index: number, value: string) => {
    setDraftQuestions(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleDeleteQuestion = useCallback((index: number) => {
    setDraftQuestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddQuestion = useCallback(() => {
    setDraftQuestions(prev => [...prev, '']);
  }, []);

  const isSaveDisabled = !hasChanges || isSaving;

  return (
    <>
      <Header>
        <Title>Customize Chat Questions</Title>
        <Description>
          Customize the initial message and starter questions shown in the chat interface for this segment&apos;s Digital Twin.
        </Description>
      </Header>

      <EditorContainer>
        <EditorFields>
          <Field>
            <FieldLabel>Initial Message</FieldLabel>
            <MessageTextarea
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              placeholder="Enter the initial message shown when the chat opens..."
              rows={3}
            />
          </Field>

          <Field>
            <FieldLabel>Questions</FieldLabel>
            <QuestionEditList>
              {draftQuestions.map((q, i) => (
                <QuestionEditRow key={i}>
                  <QuestionInput
                    type="text"
                    value={q}
                    onChange={(e) => handleQuestionChange(i, e.target.value)}
                    placeholder="Enter a question..."
                  />
                  <QuestionDeleteBtn
                    type="button"
                    onClick={() => handleDeleteQuestion(i)}
                    aria-label={`Delete question ${i + 1}`}
                  >
                    <X size={18} />
                  </QuestionDeleteBtn>
                </QuestionEditRow>
              ))}
            </QuestionEditList>
            <AddQuestionBtn
              type="button"
              onClick={handleAddQuestion}
            >
              <PlusCircle size={14} />
              Add
            </AddQuestionBtn>
          </Field>
        </EditorFields>

        <EditActions>
          <Check
            disabled={isSaveDisabled}
            onClick={handleSave}
            size={16}
            aria-label="Save chat configuration"
          />
          <XCircle
            onClick={onCancel}
            size={16}
            aria-label="Cancel editing chat configuration"
          />
        </EditActions>
      </EditorContainer>
    </>
  );
};

export const ChatConfigForm = memo(ChatConfigFormComponent);

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: var(--gray-d);
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-m);
  margin: 0;
`;

const EditorContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const EditorFields = styled.div`
  flex: 1;
`;

const EditActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  padding-top: 4px;
  flex-shrink: 0;
`;

const Field = styled.div`
  margin-top: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--gray-d);
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
  background-color: var(--gray-xl);
  border: 1px solid var(--gray-l);
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--pri-02);
  }

  &::placeholder {
    color: var(--gray-m);
  }
`;

const QuestionEditList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuestionInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-d);
  background-color: var(--gray-xl);
  border: 1px solid var(--gray-l);
  border-radius: 4px;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--pri-02);
  }

  &::placeholder {
    color: var(--gray-m);
  }
`;

const QuestionDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--pri-04);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const AddQuestionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--pri-01);
  background-color: var(--pri-02);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;
